import { FC, ReactNode, useEffect, useState } from "react";
import { gameState, useTeamSides } from "@/services/gameState";
import { useAppDataContext } from "@context/AppContext";
import { Box, Typography } from "@mui/material";
import { Score, TeamReference } from "@/models/pdfData";
import { SwapHoriz, Timer, Videocam } from "@mui/icons-material";
import { remainingChallenges } from "@/utils/challenge-utils";
import { useGameDataContext } from "@context/GameDataContext";
import { GameProgress } from "@/models/gameState";

export type TeamStates = "normal" | "warning" | "error" | "success";

type RowType = "timeouts" | "substitutions" | "challenges" | "sets";

export type ValueType = { [key in TeamReference]: number };
export type StateType = { [key in TeamReference]: TeamStates };

const colorMap: { [key in TeamStates]: string } = {
  normal: "#ffffff00",
  warning: "#e1e131ff",
  error: "#ff0000ff",
  success: "#59e459ff",
};

const defaultValue: ValueType = {
  team1: 0,
  team2: 0,
};

const defaultState: StateType = {
  team1: "normal",
  team2: "normal",
};

const getState = (remaining: number): TeamStates => {
  if (remaining === 0) {
    return "error";
  } else if (remaining === 1) {
    return "warning";
  } else {
    return "normal";
  }
};

const symbolMap: { [key: string]: ReactNode } = {
  timeouts: <Timer sx={{ fontSize: "1em" }} />,
  substitutions: <SwapHoriz sx={{ fontSize: "1em" }} />,
  challenges: <Videocam sx={{ fontSize: "1em" }} />,
  sets: <Typography fontSize={"0.7em"}>Sätze</Typography>,
} as const;

const getBackgroundImage = (
  state: TeamStates,
  direction: "left" | "right",
  color: string,
) =>
  state !== "normal"
    ? `linear-gradient(to ${direction}, #ff000200 0%, #ff00 10%, ${color} 100%)`
    : "";

const RemainingRowMarkup: FC<{
  leftValue: number;
  rightValue: number;
  type: keyof typeof symbolMap;
  leftBackgroundImage: string;
  rightBackgroundImage: string;
  disabled?: boolean;
}> = ({
  leftValue,
  rightValue,
  type,
  leftBackgroundImage,
  rightBackgroundImage,
  disabled,
}) => {
  return (
    <>
      <Box
        sx={{
          backgroundImage: leftBackgroundImage,
          flexGrow: 1,
          textAlign: "center",
          my: "2px",
          px: 1,
          borderTopLeftRadius: "0.4em",
          borderBottomLeftRadius: "0.4em",
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <Typography fontSize={"1em"} fontFamily={"Lato"}>
          {leftValue}
        </Typography>
      </Box>
      <Box
        sx={{
          justifyContent: "center",
          display: "flex",
          opacity: disabled ? 0.5 : 1,
        }}
      >
        {symbolMap[type]}
      </Box>
      <Box
        sx={{
          backgroundImage: rightBackgroundImage,
          flexGrow: 1,
          textAlign: "center",
          my: "2px",
          px: 1,
          borderTopRightRadius: "0.4em",
          borderBottomRightRadius: "0.4em",
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <Typography fontSize={"1em"} fontFamily={"Lato"}>
          {rightValue}
        </Typography>
      </Box>
    </>
  );
};

const RemainingRow: FC<{
  type: RowType;
}> = ({ type }) => {
  if (type === "challenges") {
    return <RemainingRowChallenge />;
  } else {
    return <RemainingRowNonChallenge type={type} />;
  }
};

type RequestData<T extends Exclude<RowType, "sets">> = {
  [key in `${T}Remaining` | `${T}Used`]: Score;
};
const setRequestStates = <
  T extends Exclude<RowType, "sets">,
  S extends RequestData<T>,
>(
  requestData: S,
  type: T,
  setter: {
    setState: (state: StateType) => void;
    setValue: (value: ValueType) => void;
  },
) => {
  const remaining = requestData[`${type}Remaining`] as Score;
  setter.setState({
    team1: getState(remaining["team1"]),
    team2: getState(remaining["team2"]),
  });
  const used = requestData[`${type}Used`] as Score;
  setter.setValue({
    team1: used["team1"],
    team2: used["team2"],
  });
};

const RemainingRowNonChallenge: FC<{
  type: Exclude<RowType, "challenges">;
}> = ({ type }) => {
  const { useFirstRefPerspective } = useAppDataContext();
  const { leftTeam, rightTeam } = useTeamSides({ useFirstRefPerspective });
  const currentSet = gameState("currentSet");
  const currentSetScore = gameState("currentSetScore");
  const config = gameState("matchConfiguration");
  const [value, setValue] = useState<ValueType>(defaultValue);
  const [state, setState] = useState<StateType>(defaultState);

  useEffect(() => {
    if (type !== "sets" && currentSet) {
      setRequestStates(currentSet, type, { setState, setValue });
    }
    if (type === "sets" && currentSetScore) {
      setState({
        team1:
          currentSetScore["team1"] === config.setsToWin ? "success" : "normal",
        team2:
          currentSetScore["team2"] === config.setsToWin ? "success" : "normal",
      });

      setValue({
        team1: currentSetScore["team1"],
        team2: currentSetScore["team2"],
      });
    }
  }, [currentSet, currentSetScore, type]);

  return (
    <RemainingRowMarkup
      leftValue={value[leftTeam]}
      rightValue={value[rightTeam]}
      type={type}
      leftBackgroundImage={getBackgroundImage(
        state[leftTeam],
        "left",
        colorMap[state[leftTeam]],
      )}
      rightBackgroundImage={getBackgroundImage(
        state[rightTeam],
        "right",
        colorMap[state[rightTeam]],
      )}
    />
  );
};

const RemainingRowChallenge: FC = () => {
  const { useFirstRefPerspective, challengeOverrideRemaining, currentView } =
    useAppDataContext();
  const { game } = useGameDataContext();
  const { leftTeam, rightTeam } = useTeamSides({ useFirstRefPerspective });
  const scoreConfiguration = gameState("scoreConfiguration");
  const currentSet = gameState("currentSet");
  const currentSetScore = gameState("currentSetScore");
  const playedSets = gameState("playedSets");
  const gameProgress = gameState("gameProgress");
  const [value, setValue] = useState<ValueType>(defaultValue);
  const [state, setState] = useState<StateType>(defaultState);

  const sets = Array.from(
    {
      length:
        Object.values(playedSets).length +
        (gameProgress >= GameProgress.GAME_FINISHED ? 0 : 1),
    },
    (_, i) => i + 1,
  );

  useEffect(() => {
    if (
      !scoreConfiguration.videoChallengeAvailable ||
      (currentView === "challenge" && challengeOverrideRemaining)
    ) {
      const remainingChallengesInCurrentSet = game
        ? currentView === "tablet"
          ? game?.challengesUsed?.[currentSet.setNumber]
          : remainingChallenges(game, sets)[currentSet.setNumber]
        : undefined;
      if (remainingChallengesInCurrentSet) {
        const challengeData: RequestData<"challenges"> = {
          challengesRemaining: remainingChallengesInCurrentSet,
          challengesUsed: {
            team1: 2 - remainingChallengesInCurrentSet["team1"],
            team2: 2 - remainingChallengesInCurrentSet["team2"],
          },
        };
        setRequestStates(challengeData, "challenges", { setState, setValue });
      } else {
        setRequestStates(currentSet, "challenges", { setState, setValue });
      }
    }
  }, [
    currentSet,
    currentSetScore,
    challengeOverrideRemaining,
    currentView,
    game,
  ]);

  return (
    <RemainingRowMarkup
      leftValue={value[leftTeam]}
      rightValue={value[rightTeam]}
      type={"challenges"}
      disabled={
        currentView === "tablet" && !scoreConfiguration.videoChallengeAvailable
      }
      leftBackgroundImage={getBackgroundImage(
        state[leftTeam],
        "left",
        colorMap[state[leftTeam]],
      )}
      rightBackgroundImage={getBackgroundImage(
        state[rightTeam],
        "right",
        colorMap[state[rightTeam]],
      )}
    />
  );
};

export default RemainingRow;
